import React, { useEffect, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { cleanDbName } from '../../../utils/dbText';

import OnboardingLayout from '../components/OnboardingLayout/OnboardingLayout';
import StepHeader from '../components/StepHeader/StepHeader';
import { TextField, FieldShell } from '../components/Field/Field';
import { InputShell } from '../components/Field/Field.styles';
import MoneyField from '../components/Field/MoneyField';
import MultiImageDropzone from '../components/MultiImageDropzone/MultiImageDropzone';
import { normalizeImageFile } from '../onboardingV2.images';
import { trackImagePicked } from '../onboardingV2.analytics';
import { Button, TextButton } from '../components/Button/Button';
import Callout from '../components/Callout/Callout';
import { categoryKey } from '../onboardingV2.categories';
import { formatMoney, currencyDecimals } from '../onboardingV2.locale';
import { Fields, AlertSlot, Center, TipSlot } from './steps.styles';
import {
    SavedList,
    SavedTitle,
    SavedRow,
    SavedThumb,
    RowAction,
    AddAnother,
    Select,
    SelectChevron,
} from './ProductStep.styles';

const NAME_MAX = 120;
const NAME_COUNTER_FROM = 100;
/** Free plan's `plan_features.max_images_per_product` (staging, 2026-10): the panel allows the same. */
export const PRODUCT_PHOTO_MAX = 3;
/** Free plan product limit (API `freemiumMaxProducts`): more would fail when they're created. */
export const PRODUCTS_MAX = 20;

const schema = yup.object({
    name: yup
        .string()
        .transform((value) => cleanDbName(value))
        .required('SignUpV2.errors.productNameRequired')
        .min(2, 'SignUpV2.errors.productNameShort')
        .max(NAME_MAX, 'SignUpV2.errors.productNameLong'),
    price: yup
        .number()
        .transform((value, original) => (original === '' || original === null ? undefined : value))
        .typeError('SignUpV2.errors.priceRequired')
        .required('SignUpV2.errors.priceRequired')
        .moreThan(0, 'SignUpV2.errors.pricePositive')
        .max(999999999, 'SignUpV2.errors.priceTooHigh'),
});

/** Minimal product payload derived from ManageProductPage.normalizeData. */
export const buildProductPayload = ({ name, price }) => ({
    name: name.trim(),
    sku: '',
    description: '',
    video_url: null,
    price: Number(price),
    discount_price: null,
    active: 1,
    tags: '',
    collections_id: [],
    quantity_available: null,
    product_options: null,
    product_variants: [],
    product_images: [],
});

/** Categories and their subcategories as select options ('Ropa', 'Ropa › Camisas'). */
const categoryOptions = (categories) =>
    (categories || [])
        .filter((c) => c && typeof c === 'object' && c.label)
        .flatMap((c) => [
            { id: c.id, label: c.label },
            ...(Array.isArray(c.subcategories) ? c.subcategories : []).map((sub) => ({
                id: sub.id,
                label: `${c.label} › ${sub.label}`,
            })),
        ]);

/**
 * Step 5 · producto (activation): as many products as the merchant wants. Each one: name + price
 * required, optional category and up to PRODUCT_PHOTO_MAX photos. "Agregar otro producto" saves the
 * current one to the list and clears the form; everything is created after the account exists.
 */
const ProductStep = ({ ctx }) => {
    const {
        t,
        isDemo,
        analytics,
        onboarding,
        updateOnboarding,
        productDraft,
        updateProductDraft,
        setProductPreviewUrl,
        media,
        addProductPhotos,
        removeProductPhoto,
        goTo,
        trackStep,
        stepNumberOf,
        totalSteps,
        nextAfterProduct,
        currency,
        setCurrency,
        currencyOptions,
        moneyLocale,
        savedProducts,
        saveProduct,
        removeProduct,
        editProduct,
    } = ctx;

    const category = onboarding.category;
    const catOptions = categoryOptions(onboarding.categories);
    const [categoryId, setCategoryId] = useState(() => {
        const wanted = productDraft.categoryId;
        if (wanted && catOptions.some((o) => o.id === wanted)) return wanted;
        return catOptions[0] ? catOptions[0].id : '';
    });
    const savedPhotos = (media && media.savedProductPhotos) || {};
    // Example price in the chosen currency ('50.000' COP, '24,90' EUR…).
    const decimals = currencyDecimals(currency);
    const pricePlaceholder = new Intl.NumberFormat(moneyLocale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    }).format(decimals ? 24.9 : 50000);
    // Photos live in the flow (survive "Volver", feed the live preview); first = main photo.
    const photos = (media && media.productPhotos) || [];
    const [processing, setProcessing] = useState(0);
    const [photoError, setPhotoError] = useState(null);
    const [stage] = useState('idle'); // reserved; no async create in this step
    const [serverError, setServerError] = useState(null);
    const alertRef = useRef(null);
    const mountedRef = useRef(true);

    const {
        register,
        control,
        handleSubmit,
        formState: { errors },
        setValue,
        setFocus,
        watch,
        reset,
        getValues,
    } = useForm({
        resolver: yupResolver(schema),
        mode: 'onTouched',
        reValidateMode: 'onChange',
        defaultValues: {
            name: productDraft.name || '',
            price: productDraft.price === undefined ? '' : productDraft.price,
        },
    });

    const nameValue = watch('name') || '';
    const priceValue = watch('price');
    const formEmpty =
        !nameValue.trim() && (priceValue === '' || priceValue === undefined || priceValue === null) && !photos.length;

    useEffect(() => {
        const subscription = watch((values) => {
            updateProductDraft({ name: values.name, price: values.price });
        });
        return () => subscription.unsubscribe();
    }, [watch, updateProductDraft]);

    useEffect(
        () => () => {
            mountedRef.current = false;
        },
        []
    );

    useEffect(() => {
        if (serverError && alertRef.current) alertRef.current.focus();
    }, [serverError]);

    // Any picture (HEIC from an iPhone included) is converted to JPG/PNG/WEBP before it's added.
    const onFiles = async (files) => {
        setPhotoError(null);
        const room = PRODUCT_PHOTO_MAX - photos.length - processing;
        const picked = Array.from(files || []).slice(0, Math.max(0, room));
        if (files.length > picked.length) {
            analytics.track('onboarding_photo_limit_reached', { target: 'product', picked: files.length, max: PRODUCT_PHOTO_MAX });
            setPhotoError(t('SignUpV2.producto.photosMax', { max: PRODUCT_PHOTO_MAX }));
        }
        if (!picked.length) return;
        setProcessing((n) => n + picked.length);
        let failed = null;
        for (const file of picked) {
            const startedAt = Date.now();
            try {
                const ready = await normalizeImageFile(file);
                addProductPhotos([ready]);
                trackImagePicked(analytics, { target: 'product', file, ready, startedAt });
            } catch (error) {
                trackImagePicked(analytics, { target: 'product', file, error, startedAt });
                failed = error && error.message === 'too big' ? 'SignUpV2.errors.photoSize' : 'SignUpV2.errors.photoType';
            } finally {
                if (mountedRef.current) setProcessing((n) => Math.max(0, n - 1));
            }
        }
        if (failed && mountedRef.current) setPhotoError(t(failed));
    };

    const onRemove = (id) => {
        removeProductPhoto(id);
        setPhotoError(null);
    };

    // Saves the product in the form to the list (it is created at the end, after the account exists).
    const addToList = (values) => {
        const saved = saveProduct({ name: values.name, price: values.price, categoryId: categoryId || null });
        reset({ name: '', price: '' });
        setPhotoError(null);
        return saved;
    };

    const finish = (products) => {
        const first = products[0];
        setProductPreviewUrl(null);
        updateOnboarding({
            productName: first ? first.name : null,
            productPrice: first ? first.price : null,
            productsCount: products.length,
            skippedProduct: products.length === 0,
            productCreatedAt: Date.now(),
        });
        trackStep('signup_step_completed', { products_count: products.length, currency });
        goTo(nextAfterProduct);
    };

    const onAddAnother = (values) => {
        if (processing > 0 || savedProducts.length + 1 >= PRODUCTS_MAX) return;
        addToList(values);
        setFocus('name');
    };

    const onValid = (values) => {
        if (processing > 0) return;
        setServerError(null);
        const saved = addToList(values);
        finish([...savedProducts, saved]);
    };

    // With products already in the list, an empty form just continues.
    const formIsEmpty = () => {
        const { name, price } = getValues();
        return !String(name || '').trim() && (price === '' || price === undefined || price === null) && !photos.length;
    };
    const onSubmit = (event) => {
        if (savedProducts.length && formIsEmpty()) {
            event.preventDefault();
            if (processing > 0) return;
            finish(savedProducts);
            return;
        }
        handleSubmit(onValid, onInvalid)(event);
    };

    const onInvalid = (fieldErrors) => {
        Object.keys(fieldErrors).forEach((field) =>
            analytics.track('signup_validation_error', {
                step_name: 'producto',
                field,
                error_code: fieldErrors[field].type,
            })
        );
    };

    const onLater = () => {
        updateOnboarding({ skippedProduct: true, productsCount: 0 });
        trackStep('signup_step_completed', { skipped: true });
        goTo(nextAfterProduct);
    };

    const onBack = () => {
        ctx.noteBack();
        goTo('categorias');
    };

    const busy = stage !== 'idle';
    const loadingLabel =
        stage === 'photo' ? t('SignUpV2.producto.loadingPhoto') : t('SignUpV2.producto.loading');

    const serverErrorText = {
        network: t('SignUpV2.errors.productNetwork'),
        generic: t('SignUpV2.errors.productGeneric'),
        session: t('SignUpV2.errors.session'),
        freemium: t('SignUpV2.errors.freemium'),
    };

    const nameField = register('name');

    return (
        <OnboardingLayout
            preview={ctx.preview}
            step={stepNumberOf('producto')}
            total={totalSteps}
            stepKey="producto"
            title={t('SignUpV2.producto.title')}
            headingId="producto-title"
            isDemo={isDemo}
            onBack={onBack}
            backDisabled={busy}
            onSubmit={onSubmit}
            liveMessage={busy ? loadingLabel : ''}
            footer={
                <>
                    <Button
                        type="submit"
                        iconRight="ri-arrow-right-line"
                        loading={busy}
                        loadingLabel={loadingLabel}
                        disabled={processing > 0}
                    >
                        {savedProducts.length
                            ? t('SignUpV2.producto.ctaMany', { count: savedProducts.length + (formEmpty ? 0 : 1) })
                            : t('SignUpV2.producto.cta')}
                    </Button>
                    {!savedProducts.length && (
                        <Center>
                            <TextButton tone="muted" onClick={onLater} disabled={busy}>
                                {t('SignUpV2.producto.later')}
                            </TextButton>
                        </Center>
                    )}
                </>
            }
        >
            <StepHeader
                id="producto-title"
                title={t('SignUpV2.producto.title')}
                subtitle={t('SignUpV2.producto.subtitle')}
            />
            <AlertSlot>
                {serverError && (
                    <Callout variant="error" ref={alertRef}>
                        <p>{serverErrorText[serverError]}</p>
                    </Callout>
                )}
            </AlertSlot>
            {savedProducts.length > 0 && (
                <>
                    <SavedTitle>{t('SignUpV2.producto.savedTitle', { count: savedProducts.length })}</SavedTitle>
                    <SavedList>
                        {savedProducts.map((product) => {
                            const thumb = (savedPhotos[product.id] || [])[0];
                            const cat = catOptions.find((o) => o.id === product.categoryId);
                            return (
                                <SavedRow key={product.id}>
                                    <SavedThumb src={thumb ? thumb.previewUrl : undefined}>
                                        {!thumb && <i className="ri-image-line" aria-hidden="true" />}
                                    </SavedThumb>
                                    <div className="info">
                                        <div className="name">{product.name}</div>
                                        <div className="meta">
                                            {formatMoney(product.price, currency, moneyLocale)}
                                            {cat ? ` · ${cat.label}` : ''}
                                        </div>
                                    </div>
                                    <RowAction
                                        type="button"
                                        onClick={() => {
                                            if (!formEmpty) return;
                                            editProduct(product.id);
                                            reset({ name: product.name, price: product.price });
                                            if (product.categoryId) setCategoryId(product.categoryId);
                                        }}
                                        disabled={!formEmpty}
                                        aria-label={t('SignUpV2.producto.editProduct', { name: product.name })}
                                    >
                                        <i className="ri-pencil-line" aria-hidden="true" />
                                    </RowAction>
                                    <RowAction
                                        type="button"
                                        onClick={() => removeProduct(product.id)}
                                        aria-label={t('SignUpV2.producto.removeProduct', { name: product.name })}
                                    >
                                        <i className="ri-delete-bin-line" aria-hidden="true" />
                                    </RowAction>
                                </SavedRow>
                            );
                        })}
                    </SavedList>
                </>
            )}
            <Fields disabled={busy} aria-busy={busy}>
                <TextField
                    id="product-name"
                    label={t('SignUpV2.producto.nameLabel')}
                    placeholder={t(categoryKey(category, 'namePlaceholder'))}
                    type="text"
                    autoComplete="off"
                    autoCapitalize="sentences"
                    enterkeyhint="next"
                    maxLength={NAME_MAX}
                    error={errors.name ? t(errors.name.message) : null}
                    counter={
                        nameValue.length >= NAME_COUNTER_FROM
                            ? `${nameValue.length}/${NAME_MAX}`
                            : null
                    }
                    showClear={!!nameValue && !busy}
                    onClear={() => {
                        setValue('name', '', { shouldValidate: false });
                        setFocus('name');
                    }}
                    {...nameField}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            event.preventDefault();
                            setFocus('price');
                        }
                    }}
                />
                <Controller
                    name="price"
                    control={control}
                    render={({ field }) => (
                        <MoneyField
                            id="product-price"
                            label={t('SignUpV2.producto.priceLabel')}
                            placeholder={pricePlaceholder}
                            currency={currency}
                            locale={moneyLocale}
                            currencyOptions={currencyOptions}
                            onCurrencyChange={setCurrency}
                            enterkeyhint="done"
                            error={errors.price ? t(errors.price.message) : null}
                            ref={field.ref}
                            name={field.name}
                            value={field.value}
                            onBlur={field.onBlur}
                            onChange={field.onChange}
                        />
                    )}
                />
                {catOptions.length > 0 && (
                    <FieldShell id="product-category" label={t('SignUpV2.producto.categoryLabel')} optional>
                        <InputShell>
                            <Select
                                id="product-category"
                                value={categoryId}
                                onChange={(event) => {
                                    setCategoryId(event.target.value);
                                    updateProductDraft({ categoryId: event.target.value || null });
                                }}
                            >
                                <option value="">{t('SignUpV2.producto.categoryNone')}</option>
                                {catOptions.map((option) => (
                                    <option key={option.id} value={option.id}>
                                        {option.label}
                                    </option>
                                ))}
                            </Select>
                            <SelectChevron className="ri-arrow-down-s-line" aria-hidden="true" />
                        </InputShell>
                    </FieldShell>
                )}
                <MultiImageDropzone
                    id="product-photo"
                    label={t('SignUpV2.producto.photoLabel')}
                    hint={t('SignUpV2.producto.photoHint', { max: PRODUCT_PHOTO_MAX })}
                    photos={photos}
                    max={PRODUCT_PHOTO_MAX}
                    processing={processing}
                    disabled={busy}
                    error={photoError}
                    onFiles={onFiles}
                    onRemove={onRemove}
                />
                <AddAnother>
                    {savedProducts.length + 1 >= PRODUCTS_MAX ? (
                        <Callout variant="info">
                            <p>{t('SignUpV2.producto.limitReached', { max: PRODUCTS_MAX })}</p>
                        </Callout>
                    ) : (
                        <Button
                            type="button"
                            variant="secondary"
                            iconLeft="ri-add-line"
                            onClick={handleSubmit(onAddAnother, onInvalid)}
                            disabled={processing > 0 || busy}
                        >
                            {t('SignUpV2.producto.addAnother')}
                        </Button>
                    )}
                </AddAnother>
            </Fields>
            <TipSlot>
                <Callout variant="tip" title={t('SignUpV2.producto.tipTitle')}>
                    <p>{t(categoryKey(category, 'tip'))}</p>
                </Callout>
            </TipSlot>
        </OnboardingLayout>
    );
};

export default ProductStep;
